import React from "react"
import { graphql, Link } from "gatsby"

import Layout from "../../components/Layout"
import { getOffer } from "../../data/offers"

import "../../styles/servicePage.scss"

/**
 * /services/staffaugmentation/ — the capacity practice.
 *
 * Rebuilt to the /services/aisolution/ template. The page it replaces was a
 * hero, a six-card "Why Teams Trust Us" grid, the six developer-role cards, the
 * six-step chevron and the sitewide client-stories strip.
 *
 * INDEXED. Frozen and byte-identical to git HEAD: the <h1>, the <title>, the
 * meta description, the canonical and the URL.
 *
 * ONE h1. The previous page rendered FOUR, the most on the site: antd's
 * Typography.Title defaults to h1, and Whychooseourdevelopers, the role cards
 * and the chevron wrapper each declared their own <h1 className="heading">.
 *
 * ── EIGHT SECTIONS, NOT NINE. There is no PROOF section, and it is not an
 * omission by accident. The brief scopes proof to "clients from this discipline
 * only", and no case study on this site is an augmentation engagement: all
 * eleven are delivered-project stories, and across every one of them the words
 * augment, embedded, staffing and alongside appear zero times. Borrowing a
 * project case study and presenting it as evidence of embedded engineers would
 * be inventing proof, so the section is left out and reported instead. The
 * `sp--noproof` modifier keeps the ground alternating across the gap it leaves.
 *
 * ── THE RATES IN SECTION 4 ARE UNCHANGED AND UNVERIFIED. They are rendered
 * verbatim from the copy that was in OurAugmentationOfferings.js, on
 * instruction, pending a decision. Two of the six contradict the Development
 * Month offer: React is $3,500 here against frontend $3,000 there, and Odoo is
 * $3,000 here against $3,500 there — inverted, not just apart. They are also
 * ungated, while Development Month is gated. Rendering them is not endorsing
 * them, and this comment exists so the next person does not assume they were
 * checked.
 *
 * Because of that, prices appear in TWO places on this page — the role cards and
 * the offer rows — where every other service page has them in one.
 *
 * The chevron is gone. It was the only one of the three instances with copy
 * genuinely its own, so section 5 reworks that material into named phases
 * rather than discarding it. SimplifiedStepstoEngageDevelopersWithUs,
 * ServiceStepsProcess and WEB_DevelopmentProcess are left on disk untouched;
 * this page was their last consumer, so all three are now unreferenced.
 */

const OFFER_SLUGS = ["development-month", "embedded-delivery-pod", "care-plan"]

// Resolved from the catalogue so a price, a timeline or a slug can never drift
// out of sync with the offer page it points at.
const PRACTICE_OFFERS = OFFER_SLUGS.map(getOffer).filter(Boolean)

/* 2. The three audiences the brief named — product companies with a roadmap and
   no engineers, teams where every feature waits on the same two people, and
   businesses that need capacity without a hiring cycle — two problems each. */
const PROBLEMS = [
  "You have a roadmap, a budget and nobody to build it, and the first hire is still three months of interviews away.",
  "Every feature on your board is waiting on the same two people, and both of them are also the only ones who can fix production.",
  "A hiring cycle costs you a quarter before anyone writes a line of code, and you are not certain the headcount is permanent.",
  "You found the right candidate and lost them to a company that moved faster.",
  "The work you need done is three months long, which is the worst possible length: too big for your team, too small to hire for.",
  "The last contractor delivered something nobody on your team can maintain, and you only found out when they left.",
]

/* 4. The developer roles. KEPT from OurAugmentationOfferings.js — the brief was
   explicit that these are genuinely useful — and moved into section 4.

   TITLE, EMPLOYMENT, AVAILABILITY, TAGS, LINK AND RATE ARE VERBATIM. The tags
   in particular carry over fifty technology terms that exist nowhere else on the
   page, so they are data, not decoration. Only `desc` is rewritten, from third
   person about us ("Discover a wide array of top ReactJS experts", "Our
   professionals are skilled in") to second person about the reader.

   DO NOT EDIT THE RATES. See the file header. */
const ROLES = [
  {
    title: "Senior React.js Frontend Developer",
    employment: "Full Time",
    availability: "Available Now",
    desc:
      "You get a frontend engineer who has shipped production interfaces rather than practised on them — at home in React Router, Redux and GraphQL, and able to pick up your component conventions instead of importing their own.",
    tags: [
      "Next.js",
      "Gatsby",
      "Redux",
      "Axios",
      "Formik",
      "React Router",
      "Material-UI",
      "ANT Design",
      "React Query",
      "Styled-components",
    ],
    link: "/services/staffAugmentationLetsDiscuss?topic=rjs",
    rate: "$3,500/Month",
  },
  {
    title: "Microsoft Dynamics 365 F&O Developer",
    employment: "Full Time",
    availability: "Available Now",
    desc:
      "You get someone who has already worked inside a Dynamics 365 Finance and Operations environment, D365 for short — X++, the Power Platform, and the D365 integrations that break when Microsoft ships an update — rather than a generalist reading the documentation for the first time on your project.",
    tags: [
      "X++ Language",
      "Retail Module",
      "Supply Chain",
      "Integration & Extensions",
      "Reporting & Analytics",
      "Upgrade & Migration",
    ],
    link: "/services/staffAugmentationLetsDiscuss?topic=drd",
    rate: "$4,000/Month",
  },
  {
    title: "Senior .NET / C# Backend Developer",
    employment: "Full Time",
    availability: "Available Now",
    desc:
      "You get a backend engineer fluent in ASP.NET Core, C# and the .NET ecosystem, used to working in a codebase somebody else designed and to leaving it more maintainable than they found it.",
    tags: [
      "ASP.NET Core",
      "C#",
      "SignalR",
      "Dapper",
      "Azure SDK",
      "AutoMapper",
      "Hangfire",
      "Json.NET",
    ],
    link: "/services/staffAugmentationLetsDiscuss?topic=ned",
    rate: "$3,000/Month",
  },
  {
    title: "Flutter Mobile App Developer",
    employment: "Full Time",
    availability: "Available Now",
    desc:
      "You get a cross-platform mobile engineer working in Dart and Flutter, including the UI/UX detail and the offline behaviour that decide whether an app survives its first week on a real device.",
    tags: [
      "Dart",
      "Firebase",
      "GetX",
      "RxDart",
      "Riverpod",
      "Hive",
      "Flutter Hooks",
      "Dio",
    ],
    link: "/services/staffAugmentationLetsDiscuss?topic=fed",
    rate: "$3,000/Month",
  },
  {
    title: "Odoo ERP Developer & Consultant",
    employment: "Full Time",
    availability: "Available Now",
    desc:
      "You get an engineer who has done Odoo customisation in Python against a live business rather than a demo database, and who will tell you when a configuration change would do the job that you were about to pay for as code.",
    tags: [
      "Odoo Framework",
      "Python",
      "PostgreSQL",
      "QWeb",
      "OWL Components",
      "XML-RPC",
      "External APIs",
    ],
    link: "/services/staffAugmentationLetsDiscuss?topic=oed",
    rate: "$3,000/Month",
  },
  {
    title: "AI / ML Engineer",
    employment: "Full Time",
    availability: "Available Now",
    desc:
      "You get an engineer who has taken machine learning models into production, not only into a notebook — large language model, or LLM, applications, recommendation systems and intelligent automation built against your own data and measured on it.",
    tags: [
      "Python",
      "TensorFlow",
      "PyTorch",
      "LangChain",
      "OpenAI API",
      "Scikit-learn",
      "Hugging Face",
      "MLOps",
      "RAG",
      "Vector DBs",
    ],
    link: "/services/staffAugmentationLetsDiscuss?topic=aie",
    rate: "$5,000/Month",
  },
]

/* 5. Named phases, no numbers and no dates. This reworks the material from the
   chevron this page used to carry — which was the only one of its three
   instances with copy of its own — rather than discarding it. */
const PHASES = [
  {
    phase: "Before anyone is proposed",
    title: "What the gap actually is",
    body:
      "You describe the work rather than the job title, because those are different things and the second one is often wrong. What comes back is an honest read on whether you need one engineer, a pair, or nobody yet.",
  },
  {
    phase: "Choosing who",
    title: "Meet the engineer, not a CV",
    body:
      "You interview the person who would actually be doing the work, and you can turn them down. Nobody is vetted on your behalf and presented as a fait accompli, because the only assessment that matters is whether your team wants to work with them.",
  },
  {
    phase: "Agreeing the terms",
    title: "Written down, including the exit",
    body:
      "Notice, rate, working hours, how a problem gets escalated and who to escalate it to — agreed in writing before anyone starts. The length of the commitment is part of that conversation rather than a surprise in a renewal.",
  },
  {
    phase: "The first weeks",
    title: "Onboarded into your process, not ours",
    body:
      "Your augmented engineer joins your stand-up, your board and your repository, and works to your definition of done. Nobody can integrate with a team and ramp up on a codebase at the same time without it showing, so ramp-up is planned for rather than pretended away, and the early sprints are scoped knowing somebody is still learning your code.",
  },
  {
    phase: "For as long as it runs",
    title: "An account manager who stays after kickoff",
    body:
      "One person outside the engineering relationship stays responsible for it, so a problem with the arrangement has somewhere to go that is not the developer you work with daily. Reviews look at whether the capacity is still the right shape, including when the answer is that you no longer need it.",
  },
]

/* 8. Questions about the discipline, not about any one offer. */
const FAQS = [
  {
    q: "Who manages the work — you or us?",
    a: "You do. Your engineer takes direction from your lead, works your tickets and attends your ceremonies, and that is the whole point of the arrangement: it adds capacity without adding a management layer. What sits on this side is the employment relationship and an account manager for the arrangement itself, not the work.",
  },
  {
    q: "Who owns the code?",
    a: "You do, from the first commit — it lands in your repository under your licence, and nothing is retained on this side. That is worth checking in any contract of this kind, because it is the one term that is genuinely painful to discover you got wrong after the engagement ends.",
  },
  {
    q: "How is this different from outsourcing a project?",
    a: "Outsourcing hands over an outcome: you agree a scope and a price, and somebody else decides how it gets built. Augmentation hands over nothing — you keep the decisions, the backlog and the architecture, and buy hands to execute them. If you would rather not run the work at all, a fixed-scope project is the honest answer and three of them are listed below.",
  },
  {
    q: "Can we see what these developers have actually shipped?",
    a: "Yes, before you commit rather than after. The engineers here have put production systems into finance, logistics, healthcare and retail, and you can review the relevant work and interview the person directly. Expect that of any supplier — a rate quoted against a CV you cannot check is not much of an offer.",
  },
  {
    q: "What happens if it is not working out?",
    a: "You say so and it stops, on the notice agreed at the start. A replacement is one option, and no replacement is the other; nobody stays on your account because a contract makes it awkward to leave. That is easier to promise up front than to honour later, which is why the notice period is written down rather than discussed.",
  },
  {
    q: "Is this cheaper than hiring?",
    a: "Usually on the total, and always on the timing. There is no recruitment fee, no notice period at the far end and no overhead of a permanent seat, and the cost stops when the work does. Where it is not cheaper is a role you will genuinely need for years — at that point hiring is the better deal and you should be told so.",
  },
]

const StaffAugmentation = ({ data }) => (
  <Layout
    className="services-page-container"
    data={data}
    // FROZEN — byte-identical to git HEAD. Do not touch.
    pageTitle={"Visiomate - Staff Augmentation Services"}
    descrip={
      "Scale your team seamlessly with staff augmentation services. Access top tech talent on-demand, reduce costs, and meet project deadlines. Contact us today!"
    }
  >
    {/* sp--noproof: no case study on this site is an augmentation engagement, so
        there is no section 7. The modifier keeps the grounds alternating. */}
    <article className="sp sp--noproof">
      {/* ── 1. HERO — FULL WIDTH, no split, no rail ─────────────────────────── */}
      <section className="sp-hero">
        <div className="sp-hero__inner">
          <span className="sp-tag">Capacity practice</span>
          {/* FROZEN h1. The only h1 on the page. */}
          <h1 className="sp-hero__h1">
            Scale Your Team with AI-Ready Tech Talent, On Demand
          </h1>
          <p className="sp-hero__lede">
            Your roadmap is not waiting for your hiring cycle, and neither are
            your competitors. What you need is senior engineers, full-stack
            developers and specialists who join your team, work your board and
            take direction from your lead — capacity you can add this month and
            stop when the work does, without the overhead of permanent
            headcount.
          </p>
          <Link to="/contact" className="sp-hero__cta">
            Tell us what you need built
          </Link>
        </div>
      </section>

      {/* ── 2. PROBLEMS — two columns, hairlines, no numbers, no cards ──────── */}
      <section className="sp-problems">
        <div className="sp-problems__inner">
          <span className="sp-tag">Where this starts</span>
          <h2 className="sp-h2">The problems this practice solves</h2>
          <p className="sp-lede">
            None of these are talent problems exactly. They are timing problems
            — the work is here now and the person to do it is a quarter away —
            and that is a different thing to solve.
          </p>
          <ul className="sp-problems__grid">
            {PROBLEMS.map(item => (
              <li className="sp-problems__item" key={item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 3. WHAT THIS ACTUALLY IS — plain language, no bare acronyms ─────── */}
      <section className="sp-what">
        <div className="sp-what__inner">
          {/* FIX 1 — two columns. This ran 300+ words in one narrow left
              column with the right half of the page empty. The heading and a
              standfirst carry the point on the left; the argument is broken
              into its own divisions on the right, at a 34rem measure. */}
          <div className="sp-what__cols">
            <div className="sp-what__lead">
              <span className="sp-tag">In plain terms</span>
              <h2 className="sp-h2">What this actually is</h2>
              <p className="sp-what__standfirst">
                What separates these arrangements is not the rate. It is who
                decides what gets built each week, and who owns what you are
                left holding at the end.
              </p>
            </div>
            <div className="sp-what__body">
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">What each arrangement is</h3>
              <p>
                Three arrangements get sold under similar language and they are not the
                same purchase. Augmentation puts an engineer inside your team: you
                direct the work, they attend your stand-up, and you are buying capacity.
                Outsourcing hands the whole thing over: you agree an outcome and a
                price, somebody else decides how it is built, and you are buying a
                result. An agency contract usually sits between the two — a team you
                brief rather than manage, with an account layer in between and a
                statement of work that defines what you can ask for.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">Who manages the work</h3>
              <p>
                The distinction that matters is who manages the work, because that is
                the cost nobody quotes. Under augmentation, that is you. Your lead
                assigns the tickets, sets the standard for done and notices on a Tuesday
                that something is drifting. It only works if you have somebody to do
                that. If you do not, augmentation is the wrong purchase and it will look
                like an engineering failure when it is really a management gap — which
                is worth knowing before you buy rather than three months in.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">Who owns the code</h3>
              <p>
                And then who owns the code, which should be the shortest conversation
                and is often the most expensive one. Under a sane arrangement the answer
                is you, from the first commit, in your own repository under your own
                licence, with nothing held back on the supplier's side. Check it in
                writing whoever you use. The alternative only becomes visible at the
                end, when you want to move the work and discover something essential to
                it belongs to somebody else.
              </p>
            </div>
            </div>
          </div>
          <p className="sp-what__pull">
            The useful question is not how quickly somebody can start. It is who on
            your side will be deciding what they do each week — because if the
            answer is nobody, more hands will make it worse.
          </p>
        </div>
      </section>

      {/* ── 4. THE ROLES — THE DARK BAND. Cards kept from the previous page ─── */}
      <section className="sp-build">
        <div className="sp-build__inner">
          <span className="sp-tag">The roles</span>
          <h2 className="sp-h2">Who you can put on your roadmap</h2>
          <p className="sp-lede">
            Six roles, with what each one is fluent in. Most engagements are one
            or two of these rather than a team.
          </p>
          <div className="sp-build__grid">
            {ROLES.map(r => (
              <div className="sp-build__card sp-role" key={r.title}>
                {/* The title gets the full width. Measured: with the rate
                    alongside it on a baseline row, "Odoo ERP Developer &
                    Consultant" and "Flutter Mobile App Developer" both wrapped
                    to three lines in a 344px card, because the nowrap rate was
                    taking the space they needed. */}
                <h3 className="sp-build__cardtitle">{r.title}</h3>
                <p className="sp-role__meta">
                  <span className="sp-role__employment">{r.employment}</span>
                  <span className="sp-role__avail">{r.availability}</span>
                </p>
                <p className="sp-build__cardbody">{r.desc}</p>
                <ul className="sp-build__stack">
                  {r.tags.map(tag => (
                    <li className="sp-build__stackitem" key={tag}>
                      {tag}
                    </li>
                  ))}
                </ul>
                {/* Rate and action together, at the foot: the two things a
                    buyer pairs. THE RATE IS VERBATIM — see the file header. */}
                <div className="sp-role__foot">
                  <span className="sp-role__rate">{r.rate}</span>
                  <Link to={r.link} className="sp-role__cta">
                    Discuss this role &rarr;
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. HOW WE WORK — named phases, no week numbers, no dates ────────── */}
      <section className="sp-how">
        <div className="sp-how__inner">
          <span className="sp-tag">How the work runs</span>
          <h2 className="sp-h2">How open-ended work is run</h2>
          <p className="sp-lede">
            An engagement here has no delivery date, because there is nothing
            being delivered — you are adding capacity to a roadmap that is
            already yours. What you get instead is a clear way in and a clear
            way out.
          </p>
          <ol className="sp-how__steps">
            {PHASES.map(ph => (
              <li className="sp-how__step" key={ph.title}>
                <span className="sp-how__marker" aria-hidden="true" />
                <span className="sp-how__phase">{ph.phase}</span>
                <h3 className="sp-how__title">{ph.title}</h3>
                <p className="sp-how__body">{ph.body}</p>
              </li>
            ))}
          </ol>
          <p className="sp-how__note">
            No date is published for work of this shape, and none is implied. If
            what you need is a scope and a date you can hold someone to, the
            packages below carry both.
          </p>
        </div>
      </section>

      {/* ── 6. FIXED-SCOPE OPTIONS — rows, prices shown ─────────────────────── */}
      <section className="sp-offers">
        <div className="sp-offers__inner">
          <span className="sp-tag">Fixed-scope options</span>
          <h2 className="sp-h2">Some of this work has a written scope already</h2>
          <p className="sp-lede">
            Three arrangements in this practice have been run enough times to
            state upfront what they contain, how they are billed and what they
            cost.
          </p>
          <ul className="sp-offers__list">
            {PRACTICE_OFFERS.map(o => (
              <li className="sp-offers__item" key={o.slug}>
                <Link to={`/offers/${o.slug}`} className="sp-offers__row">
                  <span className="sp-offers__main">
                    <span className="sp-offers__name">{o.name}</span>
                    <span className="sp-offers__problem">{o.symptoms[0]}</span>
                  </span>
                  <span className="sp-offers__meta">
                    <span className="sp-offers__timeline">
                      {o.timelineShort}
                    </span>
                    <span className="sp-offers__price">{o.priceShort}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="sp-offers__all">
            <Link to="/offers">Every offer, across every practice &rarr;</Link>
          </p>
        </div>
      </section>

      {/* ── 7. PROOF — DELIBERATELY ABSENT. See the file header: no case study
             on this site is an augmentation engagement, and inventing one is
             not an option. ────────────────────────────────────────────────── */}

      {/* ── 8. QUESTIONS — about the discipline ─────────────────────────────── */}
      <section className="sp-faq">
        <div className="sp-faq__inner">
          <span className="sp-tag">Questions</span>
          <h2 className="sp-h2">What buyers ask about added capacity</h2>
          <ul className="sp-faq__list">
            {FAQS.map(f => (
              <li className="sp-faq__item" key={f.q}>
                <p className="sp-faq__q">{f.q}</p>
                <p className="sp-faq__a">{f.a}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 9. IF YOUR NEED IS BROADER — one CTA ────────────────────────────── */}
      <section className="sp-broader">
        <div className="sp-broader__inner">
          <span className="sp-tag">If your need is broader</span>
          <h2 className="sp-h2">If none of that is quite your situation</h2>
          <div className="sp-broader__cols">
            <p className="sp-broader__text">
              Plenty of what lands in this practice does not fit a role card: a
              specialism nobody on your team has and nobody needs full time, a
              handover from a supplier who is leaving, someone to hold a live
              system while you recruit properly, or a second pair of eyes on a
              codebase before you commit to it.
            </p>
            <p className="sp-broader__text">
              Describe what you are dealing with in a free consultancy session
              and you will get a straight read on how it would be approached,
              what it depends on, and where the risk sits. If the answer is that
              you should hire rather than do this, that is what you will hear.
            </p>
          </div>
          <div className="sp-broader__foot">
            <Link to="/contact" className="sp-broader__cta">
              Tell us what you are dealing with
            </Link>
          </div>
        </div>
      </section>
    </article>
  </Layout>
)

export default StaffAugmentation

export const staffAugmentationQuery = graphql`
  query StaffaugmentationQuery {
    bgImage: file(relativePath: { eq: "Mask_group8.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
